var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useState, useCallback, useMemo, useContext } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import ClickAwayListener from 'react-click-away-listener';
import { translate } from '@utils/i18n';
import { Button } from '@ui';
import { CalendarContext } from '../../CalendarContext';
import { BasicSelect } from '../../../../components/BasicSelect/BasicSelect';
import { EntityTypePrefix } from '../../constants';
import './StatusesStyles.scss';
const namespace = 'features.calendar.header.statuses';
const MenuOptions = {
    Inspection: 'inspection',
    Service: 'service'
};
function StatusIcon({ status }) {
    return (React.createElement("span", { className: "qmb-status" },
        React.createElement("i", { style: { color: status.color }, className: status.icon })));
}
function Statuses() {
    const { state: { statuses }, visitStatusesForHighlight, inspectionStatusesForSelect, permissions, actions: { onToggleStatus, toggleUnselectAllInspectionStatuses, toggleUnselectAllVisitStatuses } } = useContext(CalendarContext);
    const [inspSearchText, setInspSearchText] = useState('');
    const [visitSearchText, setVisitSearchText] = useState('');
    const anchor = useRef(null);
    const popup = useRef(null);
    const [open, setOpenMainMenu] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);
    const menuAnchor = useRef(null);
    const selectedStatusIds = useMemo(() => inspectionStatusesForSelect
        .filter((status) => statuses[`Inspection${status.value}`])
        .concat(visitStatusesForHighlight.filter((status) => statuses[`Visit${status.value}`])), [statuses]);
    const onAddEventPopup = useCallback(() => {
        setOpenMainMenu(!open);
    }, [open]);
    const inspectionOptions = useMemo(() => inspectionStatusesForSelect.map((_a) => {
        var { label } = _a, statusInfo = __rest(_a, ["label"]);
        return (Object.assign({ title: label }, statusInfo));
    }), [inspectionStatusesForSelect]);
    const visitOptions = useMemo(() => visitStatusesForHighlight.map((_a) => {
        var { label } = _a, statusInfo = __rest(_a, ["label"]);
        return (Object.assign({ title: label }, statusInfo));
    }), [visitStatusesForHighlight]);
    const inspectionStatusesValue = useMemo(() => (Object.keys(statuses) || [])
        .filter((key) => key.startsWith(EntityTypePrefix.Inspection) && statuses[key])
        .map((key) => key.split(EntityTypePrefix.Inspection)[1]), [statuses]);
    const visitStatusesValue = useMemo(() => (Object.keys(statuses) || [])
        .filter((key) => key.startsWith(EntityTypePrefix.Visit) && statuses[key])
        .map((key) => key.split(EntityTypePrefix.Visit)[1]), [statuses]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { isMenu: true, type: "button", ref: anchor, onClick: onAddEventPopup },
            translate('title', { namespace }),
            selectedStatusIds.length > 0 && (React.createElement("span", { className: "control__collection" },
                selectedStatusIds.slice(0, 5).map((status, index) => (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                React.createElement(StatusIcon, { key: index, status: status }))),
                selectedStatusIds.length > 5 && (React.createElement("span", { className: "control__count" },
                    "+",
                    selectedStatusIds.length - 5))))),
        React.createElement(Popup, { anchor: anchor.current, show: open, ref: popup, id: "statushighlight-parent-popup" },
            React.createElement(ClickAwayListener, { onClickAway: () => {
                    setInspSearchText('');
                    setVisitSearchText('');
                    setActiveMenu(null);
                    setOpenMainMenu(false);
                } },
                React.createElement("div", { className: "qmb-popup--actions--y-max highlightStatusContainer multiSelectPopupContainer" },
                    React.createElement("div", { className: "qmb-popup--actions--y-max", ref: menuAnchor },
                        React.createElement("button", { className: "qmb-control--menu buttonContainer", type: "button", onClick: () => setActiveMenu(MenuOptions.Inspection) },
                            React.createElement("span", null, translate('inspection_statuses', { namespace })),
                            React.createElement("span", { className: "counter" }, inspectionStatusesValue.length > 0 ? inspectionStatusesValue.length : null)),
                        permissions.hasService && (React.createElement("button", { className: "qmb-control--menu buttonContainer", type: "button", onClick: () => setActiveMenu(MenuOptions.Service) },
                            React.createElement("span", null, translate('wo_statuses', { namespace })),
                            React.createElement("span", { className: "counter" }, visitStatusesValue.length > 0 ? visitStatusesValue.length : null))),
                        inspectionStatusesValue.length + visitStatusesValue.length > 0 && (React.createElement(React.Fragment, null,
                            React.createElement("hr", { className: "popup__split" }),
                            React.createElement("div", { className: "popup__section" },
                                React.createElement("ul", { role: "presentation" },
                                    React.createElement("li", null,
                                        React.createElement("div", { className: "messageContainer" },
                                            React.createElement(Button, { variant: "highlighted", onClick: () => {
                                                    toggleUnselectAllInspectionStatuses();
                                                    toggleUnselectAllVisitStatuses();
                                                    setActiveMenu(null);
                                                    setOpenMainMenu(false);
                                                } },
                                                React.createElement("i", { className: "fa-light fa-arrow-rotate-left" }),
                                                translate('reset', { namespace: 'filters' }))))))))),
                    React.createElement(Popup, { anchor: document.getElementById('statushighlight-parent-popup'), show: activeMenu === MenuOptions.Inspection, anchorAlign: { horizontal: 'right', vertical: 'top' }, popupAlign: { horizontal: 'left', vertical: 'top' } },
                        React.createElement(BasicSelect, { disableDebounce: true, searchText: inspSearchText, onChangeSearchText: setInspSearchText, name: "inspection", disableSorting: true, value: inspectionStatusesValue, fixedOptions: inspectionOptions, onReset: () => {
                                setInspSearchText('');
                                toggleUnselectAllInspectionStatuses();
                            }, placeholder: `Search ${translate(`plural_inspection_status`, { namespace: 'filters' })}`, onChange: (value) => {
                                const nextValues = value.map(String);
                                const statusChanged = [...nextValues, ...inspectionStatusesValue].find((val) => (!inspectionStatusesValue.includes(val) && nextValues.includes(val)) ||
                                    (inspectionStatusesValue.includes(val) && !nextValues.includes(val)));
                                if (!statusChanged)
                                    return;
                                onToggleStatus(`${EntityTypePrefix.Inspection}${statusChanged}`);
                            } })),
                    React.createElement(Popup, { anchor: document.getElementById('statushighlight-parent-popup'), show: activeMenu === MenuOptions.Service, anchorAlign: { horizontal: 'right', vertical: 'top' }, popupAlign: { horizontal: 'left', vertical: 'top' } },
                        React.createElement(BasicSelect, { disableDebounce: true, searchText: visitSearchText, onChangeSearchText: setVisitSearchText, name: "service", value: visitStatusesValue, fixedOptions: visitOptions, placeholder: `Search ${translate(`plural_visit_status`, { namespace: 'filters' })}`, onReset: () => {
                                setVisitSearchText('');
                                toggleUnselectAllVisitStatuses();
                            }, onChange: (value) => {
                                const nextValues = value.map(String);
                                const statusChanged = [...nextValues, ...visitStatusesValue].find((val) => (!visitStatusesValue.includes(val) && nextValues.includes(val)) ||
                                    (visitStatusesValue.includes(val) && !nextValues.includes(val)));
                                if (!statusChanged)
                                    return;
                                onToggleStatus(`${EntityTypePrefix.Visit}${statusChanged}`);
                            } })))))));
}
export default Statuses;
